import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, MoreHorizontal, Pencil, Power, Trash2, Building2, Plus,
  Loader2, ChevronRight, ChevronDown, CornerDownRight,
} from "lucide-react";
import { toast } from "sonner";
import { departmentsApi, Department, departmentLabel } from "@/lib/departments-api";
import { ApiError } from "@/lib/api";

const MAX_DEPTH = 5;

/**
 * Org structure page (ADR-0010): the Department entity is a tree — top-level
 * nodes are companies, anything below is a department / sub-unit. Rows render
 * in path order with indentation; selecting a node anywhere else in the app
 * (filters, announcements) includes its whole subtree.
 */
export default function Departments() {
  const [search, setSearch] = useState("");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [createParent, setCreateParent] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  /** Set when delete hit the 409 guard (children/references) — offers deactivate. */
  const [deleteBlocked, setDeleteBlocked] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    departmentsApi
      .listAll({ search: search || undefined, includeInactive: true })
      .then((all) => { if (!cancelled) setDepartments(all); })
      .catch((e) => { if (!cancelled) toast.error("Failed to load departments", { description: errMsg(e) }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, reloadKey]);

  // Rows arrive path-ordered from the API (parents before children). While
  // searching we show a flat path-labelled list, since ancestors may be
  // filtered out and indentation would lie.
  const searching = search.trim().length > 0;
  const hasChildren = useMemo(() => {
    const set = new Set<string>();
    departments.forEach((d) => d.parentId && set.add(d.parentId));
    return set;
  }, [departments]);

  const visibleRows = useMemo(() => {
    if (searching) return departments;
    return departments.filter((d) => {
      const ancestors = d.path.split("/").slice(0, -1);
      return !ancestors.some((id) => collapsed.has(id));
    });
  }, [departments, collapsed, searching]);

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const companies = departments.filter((d) => d.parentId === null).length;
  const active = departments.filter((d) => d.isActive).length;
  const totalEmployees = departments.reduce((sum, d) => sum + d.counts.employees, 0);

  const handleToggleActive = async (d: Department) => {
    try {
      await departmentsApi.update(d.id, { isActive: !d.isActive });
      toast.success(`${d.name} ${d.isActive ? "deactivated" : "activated"}`, {
        description: d.isActive
          ? "It and all its sub-units no longer appear in pickers; existing assignments are kept."
          : "It is available in pickers again (as long as its parents are active).",
      });
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await departmentsApi.remove(deleting.id);
      toast.success(`${deleting.name} deleted`);
      setDeleting(null);
      setDeleteBlocked(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDeleteBlocked(e.message);
      } else {
        toast.error("Delete failed", { description: errMsg(e) });
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const deactivateInstead = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await departmentsApi.update(deleting.id, { isActive: false });
      toast.success(`${deleting.name} deactivated`, {
        description: "It and its sub-units no longer appear in pickers; existing assignments are kept.",
      });
      setDeleting(null);
      setDeleteBlocked(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Deactivate failed", { description: errMsg(e) });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Companies</div><div className="mt-1 font-display text-xl font-bold">{companies}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total units</div><div className="mt-1 font-display text-xl font-bold">{departments.length}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</div><div className="mt-1 font-display text-xl font-bold text-success">{active}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Employees assigned</div><div className="mt-1 font-display text-xl font-bold">{totalEmployees}</div></Card>
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /><CardTitle className="text-base">Org structure ({departments.length})</CardTitle></div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search units..." className="h-9 w-56 pl-8" />
              </div>
              <Button size="sm" className="bg-gradient-primary hover:opacity-90" onClick={() => { setEditing(null); setCreateParent(null); setDialogOpen(true); }}>
                <Plus className="mr-1.5 h-4 w-4" /> Add company
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right" title="Directly assigned / incl. sub-units">Employees</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Sites</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading org structure...</TableCell></TableRow>}
              {!loading && departments.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  {search ? "No units match your search." : "No structure yet. Start by adding your companies, then create departments and sub-units under them."}
                </TableCell></TableRow>
              )}
              {!loading && visibleRows.map((d) => (
                <TableRow key={d.id} className={!d.isActive ? "opacity-60" : undefined}>
                  <TableCell className="text-sm font-medium">
                    {searching ? (
                      <span>{departmentLabel(d)}</span>
                    ) : (
                      <div className="flex items-center" style={{ paddingLeft: `${(d.depth - 1) * 20}px` }}>
                        {hasChildren.has(d.id) ? (
                          <button onClick={() => toggleCollapse(d.id)} className="mr-1 rounded p-0.5 text-muted-foreground hover:bg-secondary">
                            {collapsed.has(d.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </button>
                        ) : (
                          <span className="mr-1 inline-block w-[18px]">
                            {d.depth > 1 && <CornerDownRight className="h-3 w-3 text-muted-foreground/50" />}
                          </span>
                        )}
                        <span>{d.name}</span>
                        {d.parentId === null && (
                          <Badge variant="outline" className="ml-2 border-primary/30 bg-primary/5 text-[10px] text-primary">Company</Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-60 truncate text-sm text-muted-foreground">{d.description ?? "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {d.counts.employees}
                    {d.counts.subtreeEmployees !== d.counts.employees && (
                      <span className="text-muted-foreground"> / {d.counts.subtreeEmployees}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{d.counts.users}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{d.counts.sites}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={d.isActive
                      ? "border-success/30 bg-success/10 text-success"
                      : "border-muted-foreground/30 bg-muted text-muted-foreground"
                    }>{d.isActive ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          disabled={d.depth >= MAX_DEPTH}
                          onClick={() => { setEditing(null); setCreateParent(d); setDialogOpen(true); }}
                        >
                          <Plus className="mr-2 h-3.5 w-3.5" /> Add sub-unit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(d); setCreateParent(null); setDialogOpen(true); }}>
                          <Pencil className="mr-2 h-3.5 w-3.5" /> Edit / move
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(d)}>
                          <Power className="mr-2 h-3.5 w-3.5" /> {d.isActive ? "Deactivate" : "Activate"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => { setDeleteBlocked(null); setDeleting(d); }}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DepartmentDialog
        open={dialogOpen}
        editing={editing}
        createParent={createParent}
        all={departments}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); setReloadKey((k) => k + 1); }}
      />

      {/* Delete confirm — switches to a deactivate offer when the backend 409s */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteSubmitting && (setDeleting(null), setDeleteBlocked(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteBlocked ? `Cannot delete ${deleting?.name}` : `Delete ${deleting?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteBlocked ? (
                <>
                  <span className="block">{deleteBlocked}</span>
                  <span className="mt-2 block text-xs">
                    Deactivating hides it (and its sub-units) from pickers while keeping existing assignments intact.
                  </span>
                </>
              ) : (
                <>
                  <span className="block">
                    This permanently removes <span className="font-medium">{deleting ? departmentLabel(deleting) : ""}</span>.
                    Deletion is only possible while it has no sub-units and no employees, users or sites reference it.
                  </span>
                  <span className="mt-2 block text-xs font-medium text-destructive">
                    This cannot be undone. Use "Deactivate" instead if you might need it later.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
            {deleteBlocked ? (
              <AlertDialogAction onClick={(e) => { e.preventDefault(); deactivateInstead(); }} disabled={deleteSubmitting}>
                {deleteSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Deactivate instead
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                disabled={deleteSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DepartmentDialog({
  open,
  editing,
  createParent,
  all,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: Department | null;
  /** When set (and not editing), the new unit is created under this node. */
  createParent: Department | null;
  all: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", parentId: "" });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: editing?.name ?? "",
      description: editing?.description ?? "",
      parentId: editing ? editing.parentId ?? "" : createParent?.id ?? "",
    });
  }, [open, editing, createParent]);

  // Valid parents: any node that isn't the edited node or inside its subtree
  // (cycle), and that still has room below the depth cap for this subtree.
  const subtreeHeight = editing
    ? Math.max(
        ...all
          .filter((d) => d.path === editing.path || d.path.startsWith(editing.path + "/"))
          .map((d) => d.depth),
      ) - editing.depth + 1
    : 1;
  const parentOptions = all.filter((d) => {
    if (editing && (d.id === editing.id || d.path.startsWith(editing.path + "/"))) return false;
    return d.depth + subtreeHeight <= MAX_DEPTH;
  });

  const submit = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await departmentsApi.update(editing.id, {
          name,
          description: form.description.trim() || undefined,
          ...(form.parentId !== (editing.parentId ?? "")
            ? { parentId: form.parentId || null }
            : {}),
        });
        toast.success(`${name} updated`);
      } else {
        await departmentsApi.create({
          name,
          description: form.description.trim() || undefined,
          parentId: form.parentId || undefined,
        });
        toast.success(`${name} created`);
      }
      onSaved();
    } catch (e) {
      toast.error(editing ? "Could not update" : "Could not create", { description: errMsg(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const title = editing
    ? "Edit / move unit"
    : createParent
      ? `Add sub-unit under ${departmentLabel(createParent)}`
      : "Add company";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Renaming updates it everywhere. Changing the parent moves the unit with all its sub-units."
              : "Top-level entries are your companies; everything below them is a department or sub-unit."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.parentId ? "e.g. Operations" : "e.g. ABC Cleaning Sdn Bhd"} />
          </div>
          <div className="grid gap-1.5">
            <Label>Description <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this unit covers" />
          </div>
          <div className="grid gap-1.5">
            <Label>Parent</Label>
            <Select
              value={form.parentId || "root"}
              onValueChange={(v) => setForm({ ...form, parentId: v === "root" ? "" : v })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="root">— Top level (company) —</SelectItem>
                {parentOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{departmentLabel(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editing && form.parentId !== (editing.parentId ?? "") && (
              <p className="text-[11px] text-muted-foreground">
                Moving takes all sub-units along. Filters and reports follow the new position immediately.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error";
}
